import { type FC } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { useCreateNewUser } from '@features/user-management/components/modals/useCreateNewUser';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
} from '@shared/components/ui';
import { GenericButton } from '@shared/components/ui/custom';
import { cn } from '@shared/utils';

import { getFieldErrorTestId } from '../../utils/user-management.utils';

import { type CreateUserFormValues, CreateUserSchema } from './create-new-user.schema';
import { roles } from './create-new-user.types';

export const CreateNewUserForm: FC = () => {
  const { submitNewUser, isLoading, onCancel } = useCreateNewUser();

  const form = useForm<CreateUserFormValues>({
    resolver: zodResolver(CreateUserSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      roleId: '',
    },
    mode: 'onSubmit',
  });

  const onSubmit = (data: CreateUserFormValues) => {
    void submitNewUser(data, form.setError);
  };

  return (
    <Form {...form}>
      <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)} noValidate>
        <div className="flex-1 overflow-y-auto bg-background p-8">
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="max-w-[640px] space-y-6">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="first-name" className="flex items-center gap-2">
                      First Name
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter user's first name"
                        {...field}
                        data-testid="user-management-create-user-input-first-name"
                        id="first-name"
                        autoComplete="off"
                      />
                    </FormControl>
                    <FormMessage data-testid={getFieldErrorTestId(form.formState.errors.firstName?.message)} />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="last-name" className="flex items-center gap-2">
                      Last Name
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter user's last name"
                        {...field}
                        data-testid="user-management-create-user-input-last-name"
                        id="last-name"
                      />
                    </FormControl>
                    <FormMessage data-testid={getFieldErrorTestId(form.formState.errors.lastName?.message)} />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="email" className="flex items-center gap-2">
                      Email
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="Enter email address"
                        {...field}
                        data-testid="user-management-create-user-input-email"
                        id="email"
                        autoComplete="off"
                      />
                    </FormControl>
                    <FormMessage data-testid={getFieldErrorTestId(form.formState.errors.email?.message)} />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="roleId"
                render={({ field }) => (
                  <FormItem>
                    <span id="roleId-label" className="flex items-center gap-2 text-sm font-medium leading-none">
                      Select a Role
                    </span>
                    <FormControl>
                      <div
                        className={cn(
                          'border rounded-md overflow-hidden p-5',
                          form.formState.errors.roleId ? 'border-error-foreground' : 'border-border',
                        )}
                      >
                        <RadioGroup
                          value={field.value ?? ''}
                          onValueChange={field.onChange}
                          key={field.value}
                          aria-labelledby="roleId-label"
                        >
                          {roles.map((role) => (
                            <div className="flex items-start gap-3" key={role.id}>
                              <RadioGroupItem
                                value={role.id}
                                id={role.id}
                                checked={field.value === String(role.id)}
                                className="mt-1"
                              />
                              <div>
                                <Label htmlFor={role.id} className="font-medium cursor-pointer my-1.5 block">
                                  {role.label}
                                </Label>
                                <p className="text-[11px] text-muted-foreground mt-1">{role.description}</p>
                              </div>
                            </div>
                          ))}
                        </RadioGroup>
                      </div>
                    </FormControl>
                    <FormMessage data-testid={getFieldErrorTestId(form.formState.errors.roleId?.message)} />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex items-center gap-3">
              <GenericButton
                variant="secondary"
                onClick={onCancel}
                disabled={isLoading}
                data-testid="user-management-create-user-cancel-button"
                type="button"
                label="Cancel"
              />
              <GenericButton
                type="submit"
                isLoading={isLoading}
                data-testid="user-management-create-user-submit-button"
                label="Add User"
              />
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
};
